import { type FC, useMemo } from 'react';
import { type ReactDatePickerCustomHeaderProps } from 'react-datepicker';

import clsx from 'clsx';
import times from 'lodash/times';

import { Flex } from '@/components/common/Flex';
import { Grid } from '@/components/common/Grid';
import { ChevronBackIcon } from '@/components/common/icons/ChevronBackIcon';
import { ChevronForwardIcon } from '@/components/common/icons/ChevronForwardIcon';
import { Select, type SelectItem } from '@/components/common/select/Select';
import { Text } from '@/components/common/Text';
import dayjs from '@/utils/dayjs';

import styles from './DatePickerHeader.module.scss';

export const DatePickerHeader: FC<ReactDatePickerCustomHeaderProps> = (props) => {
  const { date, decreaseMonth, increaseMonth, changeYear } = props;

  const minYear = 2010;
  const maxYear = dayjs().year();

  const yearOptions = useMemo(() => {
    const options: SelectItem<number>[] = [];

    for (let year = minYear; year <= maxYear; year++) {
      options.push({
        value: year,
      });
    }

    return options;
  }, [maxYear]);

  const dayjsDate = dayjs(date);
  const currentYear = dayjsDate.year();
  const currentMonth = dayjsDate.month();
  const isDecreaseMonthDisabled = currentMonth === 0 && currentYear === minYear;
  const isIncreaseMonthDisabled = currentMonth === 11 && currentYear === maxYear;

  return (
    <Flex className={styles.root} justifyContent="spaceBetween">
      <Select value={dayjsDate.year()} options={yearOptions} onChange={changeYear} />

      <Flex className={styles.monthSelect} gap={2} alignItems="center">
        <ChevronBackIcon
          className={clsx(styles.icon, { [styles.isDisabled]: isDecreaseMonthDisabled })}
          onClick={isDecreaseMonthDisabled ? undefined : decreaseMonth}
        />

        <Grid alignItems="center" justifyItems="center">
          {times(12).map((month) => (
            <Text
              key={month}
              className={clsx(styles.month, { [styles.isCurrent]: month === currentMonth })}
              type="medium16"
              color="white"
              transform="lowercase"
            >
              {dayjsDate.month(month).format('MMMM')}
            </Text>
          ))}
        </Grid>

        <ChevronForwardIcon
          className={clsx(styles.icon, { [styles.isDisabled]: isIncreaseMonthDisabled })}
          onClick={isIncreaseMonthDisabled ? undefined : increaseMonth}
        />
      </Flex>
    </Flex>
  );
};
